import { Pipe, PipeTransform } from '@angular/core';
import { Observable, catchError, map, retry, switchMap } from 'rxjs';
import { WallDecorRenderPreviewConfig } from '../../../../../photographer-client/src/app/modules/private-sessions/graphql';

import {
  AvailableWallDecorScenesGQL,
  WallDecorPieceForDisplayType,
} from '@ui/look-book-front/wall-decor-front/graphql';
import { LookBookProductType } from '@ui/look-book-front/types';
import { LookBookProductPreviewsService } from '@ui/look-book-front/look-book-product-previews.service';
import { groupArrayByIndex } from '../../../../../../helpers/array';
import { pickImagesForThumb } from 'graphql/libs/look-book-client/src/helpers';
import { ImagesInWallDecorMap } from '@ui/look-book-front/wall-decor-front/types';
import { environment } from '@ui/look-book-front/enviroment/enviroment';
import { LookBookCanvasSceneRenderBody } from 'graphql/libs/look-book-rendering/types';
import { HttpClient } from '@angular/common/http';
import cuid from 'cuid';

@Pipe({
  name: 'lookBookSingleTriplexPreview',
  standalone: false,
})
export class LookBookSingleTriplexPreviewPipe implements PipeTransform {
  availableScenes$ = this.availableWallDecorScenesGQL
    .fetch({})
    .pipe(
      map(
        ({ data: { getAvailableWallDecorScenes } }) =>
          getAvailableWallDecorScenes,
      ),
    );

  constructor(
    private availableWallDecorScenesGQL: AvailableWallDecorScenesGQL,
    private lookBookProductPreviewsService: LookBookProductPreviewsService,
    private httpClient: HttpClient,
  ) {}

  transform(
    photoId: string,
    allImages: {
      photoId: string;
      width: number;
      height: number;
      photoGroupId: string;
    }[] = [],
  ): Observable<WallDecorRenderPreviewConfig> {
    const availableImages = [photoId];
    const indexedPhotos = groupArrayByIndex(allImages, 'photoId');

    return this.availableScenes$.pipe(
      switchMap((scenes) => {
        const filtered = scenes.filter(
          (scene) =>
            scene.labs.includes(
              this.lookBookProductPreviewsService.labs[
                LookBookProductType.Triplex
              ],
            ) &&
            scene.pieces.some(
              ({ type }) => type === WallDecorPieceForDisplayType.Tambour,
            ),
        );
        const pickedScene = pickImagesForThumb(
          allImages.filter(({ photoId }) => availableImages.includes(photoId)),
          filtered,
          1,
        );
        console.log('picked scene', pickedScene);
        const indexedPieces = groupArrayByIndex(
          pickedScene.matchingScene.pieces,
          'WallDecorPieceForDisplayId',
        );
        // @ts-ignore
        const images = Object.fromEntries(
          Object.entries(pickedScene.pieces).map(([index, value]) => {
            const imageRatio =
              indexedPhotos[value.photoId].width /
              indexedPhotos[value.photoId].height;

            const currentValue =
              indexedPieces[value.WallDecorPieceForDisplayId];
            const containerRatio = currentValue.flip
              ? currentValue.imageHeight / currentValue.imageWidth
              : currentValue.imageWidth / currentValue.imageHeight;

            return [
              index,
              {
                ...value,
                photoGroupId: indexedPhotos[value.photoId].photoGroupId,
                isFlip: value.isFlip,
                crop:
                  containerRatio > imageRatio
                    ? {
                        w: 100,
                        x: 0,
                        h: (imageRatio / containerRatio) * 100,
                        y: (100 - (imageRatio / containerRatio) * 100) / 2,
                      }
                    : {
                        h: 100,
                        y: 0,
                        w: (containerRatio / imageRatio) * 100,
                        x: (100 - (containerRatio / imageRatio) * 100) / 2,
                      },
              },
            ];
          }),
        );
        return this.previewForWallDecor(pickedScene.matchingScene, images).pipe(
          catchError(() => {
            this.lookBookProductPreviewsService.nodeId = null;
            return this.previewForWallDecor(pickedScene.matchingScene, images);
          }),
          retry(10),
          map((preview) => ({
            ...preview,
            wallDecorRenderPreviewConfigId: cuid(),
            color: '#ffffff',
          })),
        );
      }),
    );
  }

  previewForWallDecor(scene, imageMap: ImagesInWallDecorMap) {
    const size = Math.min(
      1280,
      Math.ceil((720 * window.devicePixelRatio) / 80) * 80,
    );
    const body: LookBookCanvasSceneRenderBody & { isPreview: boolean } = {
      color: '#FFFFFF',
      sceneId: scene.wallDecorSceneForDisplayId,
      nodeId: this.lookBookProductPreviewsService.nodeId,
      images: scene.pieces.reduce((prevValue, currentValue) => {
        if (imageMap[currentValue.WallDecorPieceForDisplayId]) {
          prevValue[currentValue.WallDecorPieceForDisplayId] =
            imageMap[currentValue.WallDecorPieceForDisplayId];
        }
        return prevValue;
      }, {} as ImagesInWallDecorMap),
      size,
      isPreview: true,
      pieces: [
        'front',
        ...scene.pieces.map(
          ({ WallDecorPieceForDisplayId }) => WallDecorPieceForDisplayId,
        ),
      ],
    };
    return this.httpClient
      .post<{
        fileName: string;
        token: string;
        ui: { overlayDom; overlayLayers; nodeId: string };
      }>(environment.canvasScenesUrl, body, { responseType: 'json' })
      .pipe(
        map((v) => {
          return {
            wallDecorSceneId: scene.wallDecorSceneForDisplayId,
            fileName: v.fileName,
            accessToken: v.token,
            images: Object.entries(imageMap).map(
              ([
                wallDecorPieceId,
                { photoId, photoGroupId, crop, isFlip },
              ]) => ({
                wallDecorPieceId,
                photoId,
                photoGroupId,
                crop,
                isFlip,
              }),
            ),
          };
        }),
      );
  }
}
